import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Paper as MuiPaper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import styled from "@emotion/styled";
import {
  Download,
  Search,
  Info,
  CheckCircleOutline,
  Refresh,
} from "@mui/icons-material";

import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  fetchAllPostalInvoices,
  fetchPostalInvoiceDispatches,
  markPostalInvoicePaid,
  regeneratePostalInvoicePdf,
  PostalInvoice,
} from "../../redux/slices/postalInvoice";
import PDFViewerModal from "../PDFViewer";
import { baseUrl } from "../../config";

const Paper = styled(MuiPaper)(spacing);

const statusLabel = (status: string) => {
  switch (status) {
    case "open":
      return "Avoin";
    case "paid":
      return "Maksettu";
    case "void":
      return "Hävitetty";
    case "uncollectible":
      return "Perimätön";
    case "generating":
      return "Luodaan";
    default:
      return status;
  }
};

const statusColor = (
  status: string
): "warning" | "success" | "error" | "default" => {
  switch (status) {
    case "open":
      return "warning";
    case "paid":
      return "success";
    case "void":
    case "uncollectible":
      return "error";
    default:
      return "default";
  }
};

const occasionLabel = (occ: string) => {
  switch (occ) {
    case "INVOICE":
      return "Laskut";
    case "MEETING_INVITE":
      return "Kokouskutsut";
    case "NOTICE":
      return "Tiedotteet";
    default:
      return occ;
  }
};

const formatDate = (val: string | null | undefined) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fi-FI");
};

const formatAmount = (cents: number | null | undefined) => {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface DetailModalProps {
  invoice: PostalInvoice;
  onClose: () => void;
}

const DetailModal = ({ invoice, onClose }: DetailModalProps) => {
  const dispatch = useAppDispatch();
  const dispatches = useAppSelector((s) => s.postalInvoice.dispatches);

  useEffect(() => {
    dispatch(fetchPostalInvoiceDispatches({ id: invoice.id, limit: 100 }));
  }, [dispatch, invoice.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Postilaskun tiedot</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Laskunumero
            </Typography>
            <Typography>{invoice.number}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Tiekunta
            </Typography>
            <Typography>{invoice.association?.name ?? "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Tila
            </Typography>
            <Box mt={0.5}>
              <Chip
                label={statusLabel(invoice.status)}
                color={statusColor(invoice.status)}
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Summa (€, sis. ALV)
            </Typography>
            <Typography>{formatAmount(invoice.amountGrossCents)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Laskupäivä
            </Typography>
            <Typography>{formatDate(invoice.issuedAt)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Eräpäivä
            </Typography>
            <Typography>{formatDate(invoice.dueAt)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Kausi
            </Typography>
            <Typography>
              {formatDate(invoice.periodStart)} –{" "}
              {formatDate(invoice.periodEnd)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          Yhteenveto
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tyyppi</TableCell>
              <TableCell align="right">Kpl</TableCell>
              <TableCell align="right">Sivut</TableCell>
              <TableCell align="right">Veroton</TableCell>
              <TableCell align="right">ALV</TableCell>
              <TableCell align="right">Yhteensä</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoice.summary.map((line) => (
              <TableRow key={line.occasion}>
                <TableCell>{occasionLabel(line.occasion)}</TableCell>
                <TableCell align="right">{line.letterCount}</TableCell>
                <TableCell align="right">{line.totalPages}</TableCell>
                <TableCell align="right">
                  {formatAmount(line.netCents)}
                </TableCell>
                <TableCell align="right">
                  {formatAmount(line.taxCents)}
                </TableCell>
                <TableCell align="right">
                  {formatAmount(line.grossCents)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          Yksittäiset lähetykset{" "}
          {dispatches?.total ? `(${dispatches.total})` : ""}
        </Typography>
        {!dispatches ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </Box>
        ) : dispatches.items.length === 0 ? (
          <Typography color="text.secondary">Ei lähetyksiä.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Päivä</TableCell>
                  <TableCell>Tyyppi</TableCell>
                  <TableCell>Vastaanottaja</TableCell>
                  <TableCell align="right">Sivut</TableCell>
                  <TableCell align="right">Hinta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dispatches.items.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{formatDate(d.sentAt)}</TableCell>
                    <TableCell>{occasionLabel(d.occasion)}</TableCell>
                    <TableCell>
                      {[d.recipientUser?.firstName, d.recipientUser?.lastName]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </TableCell>
                    <TableCell align="right">{d.pages}</TableCell>
                    <TableCell align="right">
                      {formatAmount(d.totalGrossCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sulje</Button>
      </DialogActions>
    </Dialog>
  );
};

const TERMINAL_STATUSES = ["paid", "void", "uncollectible"] as const;

const SuperadminPostalInvoices = () => {
  const dispatch = useAppDispatch();
  const { allInvoices, status } = useAppSelector((s) => s.postalInvoice);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState<PostalInvoice | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchAllPostalInvoices());
  }, [dispatch]);

  const handlePreview = (invoice: PostalInvoice) => {
    const filename = invoice.pdf?.filename;
    if (!filename) return;
    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(true);
  };

  const handleDownload = async (invoice: PostalInvoice) => {
    const filename = invoice.pdf?.filename;
    if (!filename) return;
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading postal invoice:", error);
    }
  };

  const handleRegeneratePdf = async (invoice: PostalInvoice) => {
    setRegeneratingId(invoice.id);
    try {
      await dispatch(regeneratePostalInvoicePdf(invoice.id)).unwrap();
    } catch (err: any) {
      const msg =
        err?.message ??
        err?.response?.data?.message ??
        "PDF:n muodostaminen epäonnistui.";
      setErrorMessage(msg);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleMarkPaid = async (invoice: PostalInvoice) => {
    try {
      await dispatch(markPostalInvoicePaid(invoice.id)).unwrap();
    } catch (err: any) {
      const msg =
        err?.message ??
        err?.response?.data?.message ??
        "Merkitseminen epäonnistui.";
      setErrorMessage(msg);
    }
  };

  const isLoading = status === "loading";

  return (
    <>
      <Helmet title="Postilaskut" />
      <Typography variant="h3" gutterBottom>
        Postilaskut
      </Typography>
      <Divider sx={{ mb: 4 }} />

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Laskunumero</TableCell>
                <TableCell>Tiekunta</TableCell>
                <TableCell align="right">Summa (€)</TableCell>
                <TableCell>Laskupäivä</TableCell>
                <TableCell>Eräpäivä</TableCell>
                <TableCell>Kausi</TableCell>
                <TableCell>Tila</TableCell>
                <TableCell align="right">Toiminnot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : allInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" py={2}>
                      Ei vielä postilaskuja.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                allInvoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.number}</TableCell>
                    <TableCell>{invoice.association?.name ?? "—"}</TableCell>
                    <TableCell align="right">
                      {formatAmount(invoice.amountGrossCents)}
                    </TableCell>
                    <TableCell>{formatDate(invoice.issuedAt)}</TableCell>
                    <TableCell>{formatDate(invoice.dueAt)}</TableCell>
                    <TableCell>
                      {formatDate(invoice.periodStart)} –{" "}
                      {formatDate(invoice.periodEnd)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel(invoice.status)}
                        color={statusColor(invoice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Tiedot">
                        <IconButton
                          size="small"
                          onClick={() => setDetailInvoice(invoice)}
                        >
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Esikatselu">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!invoice.pdf?.filename}
                            onClick={() => handlePreview(invoice)}
                          >
                            <Search fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Lataa PDF">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!invoice.pdf?.filename}
                            onClick={() => handleDownload(invoice)}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {!invoice.pdf?.filename &&
                        !TERMINAL_STATUSES.includes(
                          invoice.status as typeof TERMINAL_STATUSES[number]
                        ) && (
                          <Tooltip title="Muodosta PDF uudelleen">
                            <span>
                              <IconButton
                                size="small"
                                disabled={regeneratingId === invoice.id}
                                onClick={() => handleRegeneratePdf(invoice)}
                              >
                                {regeneratingId === invoice.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Refresh fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      <Tooltip
                        title={
                          TERMINAL_STATUSES.includes(
                            invoice.status as typeof TERMINAL_STATUSES[number]
                          )
                            ? "Lasku on jo päätöstilassa"
                            : "Merkitse maksetuksi"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={TERMINAL_STATUSES.includes(
                              invoice.status as typeof TERMINAL_STATUSES[number]
                            )}
                            onClick={() => handleMarkPaid(invoice)}
                          >
                            <CheckCircleOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {detailInvoice && (
        <DetailModal
          invoice={detailInvoice}
          onClose={() => setDetailInvoice(null)}
        />
      )}

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SuperadminPostalInvoices;
